import {
  Alert,
  AppShellContent,
  Button,
  Group,
  SegmentedFilter,
  Skeleton,
  Stack,
  Text,
  Tile,
  Title,
  toast,
} from "@assetwatch/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { getWorkScopeSearch } from "../../lib/workSearch";
import { saveProcedureAuthoring } from "../../queries/procedureAuthoringApi";
import {
  useProcedureAuthoringQuery,
  useProcedureCategoriesQuery,
} from "../../queries/useProcedureAuthoringQuery";
import { useTeamsUsersQuery } from "../../queries/useTeamsUsersQuery";
import { ProcedureAuthoringHeader } from "../snowflakes/procedures/ProcedureAuthoringHeader";
import { useWorkScope } from "../WorkScopeProvider";
import {
  readAiProcedureDraft,
  removeAiProcedureDraft,
} from "./aiProcedureDraft";
import {
  type PendingProcedureDelete,
  ProcedureDeleteDialog,
  ProcedureLeaveDialog,
} from "./ProcedureAuthoringDialogs";
import { ProcedureBuilderContent } from "./ProcedureBuilderContent";
import { ProcedurePreview } from "./ProcedurePreview";
import {
  addGroup,
  addItem,
  addSection,
  createBlankProcedure,
  createItem,
  deleteGroupKeepingContents,
  deleteItem,
  deleteSectionKeepingContents,
  duplicateGroup,
  duplicateItem,
  duplicateSection,
  flattenProcedureItems,
  insertItemAfter,
  moveItemBefore,
  moveItemToSectionBefore,
  type ProcedureAuthoringDraft,
  updateItem,
  validateProcedure,
} from "./procedureAuthoring";
import {
  useProcedureAuthoringDefaults,
  useProcedureAuthoringFocus,
  useProcedureAuthoringLoadedDraft,
  useProcedureAuthoringNavigationWarning,
} from "./useProcedureAuthoringEffects";

export function ProcedureAuthoringScreen({
  procedureId,
  aiDraftId,
}: {
  procedureId?: string;
  aiDraftId?: string;
}) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  return (
    <ProcedureAuthoringEditor
      key={`${selectedCustomer?.id ?? "none"}:${selectedFacility?.id ?? "none"}:${procedureId ?? aiDraftId ?? "new"}`}
      aiDraftId={aiDraftId}
      procedureId={procedureId}
    />
  );
}

function ProcedureAuthoringEditor({
  procedureId,
  aiDraftId,
}: {
  procedureId?: string;
  aiDraftId?: string;
}) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const teamsUsers = useTeamsUsersQuery();
  const manualProcedureQuery = useProcedureAuthoringQuery(procedureId);
  const categoryQuery = useProcedureCategoriesQuery();
  const navigate = useNavigate();
  const ids = useRef(0);
  const nextId = () =>
    globalThis.crypto?.randomUUID?.() ?? `authoring-${++ids.current}`;
  const [draft, setDraft] = useState<ProcedureAuthoringDraft>(() =>
    createBlankProcedure(nextId),
  );
  const focusNewItemId = useRef<string | null>(null);
  const focusNewStructure = useRef<{
    kind: "heading" | "section";
    id: string;
  } | null>(null);
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const [savedProcedureId, setSavedProcedureId] = useState(procedureId);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(
    draft.looseItems[0]?.id ?? null,
  );
  const [selectedStructure, setSelectedStructure] = useState<{
    kind: "heading" | "section";
    id: string;
  } | null>(null);
  const [draggingItem, setDraggingItem] = useState<{
    sectionId: string | null;
    itemId: string;
  } | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);
  const [retainedSourceName, setRetainedSourceName] = useState<string | null>(
    null,
  );
  const [pendingDelete, setPendingDelete] =
    useState<PendingProcedureDelete | null>(null);
  const [leavePrompt, setLeavePrompt] = useState(false);
  const pendingNavigationResolve = useRef<
    ((allowNavigation: boolean) => void) | null
  >(null);
  const skipNextNavigation = useRef(false);
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    JSON.stringify(draft),
  );

  useProcedureAuthoringDefaults({
    data: teamsUsers.data,
    draft,
    procedureId,
    setDraft,
  });
  useProcedureAuthoringLoadedDraft({
    data: manualProcedureQuery.data,
    flattenItems: flattenProcedureItems,
    isDirty: JSON.stringify(draft) !== savedSnapshot,
    setDraft,
    setSavedSnapshot,
    setSelectedItemId,
  });
  useEffect(() => {
    if (!aiDraftId) return;
    const envelope = readAiProcedureDraft(aiDraftId);
    if (!envelope) return;
    setDraft(envelope.draft);
    setRetainedSourceName(
      envelope.sourceMaterial.retained
        ? envelope.sourceMaterial.fileName
        : null,
    );
    setSelectedItemId(flattenProcedureItems(envelope.draft)[0]?.id ?? null);
  }, [aiDraftId]);
  useProcedureAuthoringFocus({
    focusNewItemId,
    focusNewStructure,
    selectedItemId,
    selectedStructure,
  });

  const selectedItem =
    flattenProcedureItems(draft).find((item) => item.id === selectedItemId) ??
    null;
  const isDirty = JSON.stringify(draft) !== savedSnapshot;
  const isNewAiDraft = Boolean(aiDraftId && !savedProcedureId);
  useProcedureAuthoringNavigationWarning({
    isDirty,
    onBlocked: (resolve) => {
      pendingNavigationResolve.current = resolve;
      setLeavePrompt(true);
    },
    skipNextNavigation,
  });
  function backToLibrary(force = false) {
    if (!force && isDirty) {
      setLeavePrompt(true);
      return;
    }
    if (selectedCustomer && selectedFacility)
      void navigate({
        to: "/procedures",
        search: getWorkScopeSearch(selectedCustomer, selectedFacility),
      });
  }
  function patch(patchValue: Partial<ProcedureAuthoringDraft>) {
    setDraft((current) => ({ ...current, ...patchValue }));
    setErrors({});
    setSaveError(null);
    setSavedNotice(false);
  }
  function addHeading() {
    const group = {
      id: nextId(),
      title: "",
      sections: [{ id: nextId(), title: "", items: [] }],
    };
    setDraft((current) => addGroup(current, group));
    focusNewStructure.current = { kind: "heading", id: group.id };
    setSelectedItemId(null);
    setSelectedStructure({ kind: "heading", id: group.id });
  }
  function addSectionToGroup(groupId: string | null) {
    const section = { id: nextId(), title: "", items: [] };
    setDraft((current) => addSection(current, groupId, section));
    focusNewStructure.current = { kind: "section", id: section.id };
    setSelectedItemId(null);
    setSelectedStructure({ kind: "section", id: section.id });
  }
  function addNewItem(groupId: string | null, sectionId: string | null) {
    const item = createItem(nextId());
    setDraft((current) => addItem(current, groupId, sectionId, item));
    focusNewItemId.current = item.id;
    setSelectedItemId(item.id);
  }
  function addItemAfter(
    groupId: string | null,
    sectionId: string | null,
    afterItemId: string,
  ) {
    const item = createItem(nextId());
    setDraft((current) =>
      insertItemAfter(current, groupId, sectionId, afterItemId, item),
    );
    focusNewItemId.current = item.id;
    setSelectedItemId(item.id);
  }
  function duplicateItemAndFocus(sectionId: string | null, itemId: string) {
    const duplicateId = nextId();
    setDraft((current) =>
      duplicateItem(current, sectionId, itemId, () => duplicateId),
    );
    focusNewItemId.current = duplicateId;
    setSelectedItemId(duplicateId);
  }
  function handleItemDragOver(sectionId: string | null, targetItemId: string) {
    if (
      !draggingItem ||
      draggingItem.sectionId !== sectionId ||
      draggingItem.itemId === targetItemId
    )
      return;
    setDraft((current) =>
      moveItemBefore(current, sectionId, draggingItem.itemId, targetItemId),
    );
    setDraggingItem({ sectionId, itemId: draggingItem.itemId });
  }
  function handleItemDrop(sectionId: string | null, targetItemId?: string) {
    if (!draggingItem) return;
    setDraft((current) =>
      moveItemToSectionBefore(
        current,
        draggingItem.sectionId,
        sectionId,
        draggingItem.itemId,
        targetItemId,
      ),
    );
    setDraggingItem(null);
  }
  function addChoice(itemId: string) {
    setDraft((current) =>
      updateItem(current, itemId, (item) => ({
        ...item,
        options: [...item.options, `Option ${item.options.length + 1}`],
      })),
    );
  }
  function removeChoice(itemId: string, optionIndex: number) {
    setDraft((current) =>
      updateItem(current, itemId, (item) => ({
        ...item,
        options: item.options.filter((_, index) => index !== optionIndex),
      })),
    );
  }
  function removeItem(sectionId: string | null, itemId: string) {
    if (!window.confirm("Delete this procedure item? This cannot be undone."))
      return;
    setDraft((current) => deleteItem(current, sectionId, itemId));
    setSelectedItemId(null);
  }
  function removeSection(groupId: string | null, sectionId: string) {
    const section = groupId
      ? draft.groups
          .find((group) => group.id === groupId)
          ?.sections.find((candidate) => candidate.id === sectionId)
      : draft.looseSections.find((candidate) => candidate.id === sectionId);
    if (!section) return;
    setPendingDelete({
      kind: "section",
      groupId,
      id: sectionId,
      label: section.title || "Untitled section",
      hasContents: section.items.length > 0,
      canKeepContents: groupId
        ? (draft.groups.find((group) => group.id === groupId)?.sections
            .length ?? 0) > 1
        : draft.looseSections.length > 1,
    });
  }
  function removeGroup(groupId: string) {
    const group = draft.groups.find((candidate) => candidate.id === groupId);
    if (!group) return;
    setPendingDelete({
      kind: "group",
      groupId: null,
      id: groupId,
      label: group.title || "Untitled heading",
      hasContents: group.sections.some((section) => section.items.length > 0),
      canKeepContents: draft.groups.length > 1,
    });
  }
  function save(afterSave?: () => void) {
    const currentUser = teamsUsers.data?.currentUserId
      ? teamsUsers.data.users.find(
          (user) => user.id === teamsUsers.data?.currentUserId,
        )
      : undefined;
    const categoryIds =
      draft.categoryIds.length > 0
        ? draft.categoryIds
        : draft.categories
            .map(
              (category) =>
                categoryOptions.find((option) => option.name === category)?.id,
            )
            .filter((id): id is number => id !== undefined);
    const draftToSave =
      !savedProcedureId && !draft.ownerId && !draft.owner.trim() && currentUser
        ? {
            ...draft,
            categoryIds,
            owner: currentUser.name,
            ownerId: currentUser.id,
          }
        : categoryIds.length !== draft.categoryIds.length
          ? { ...draft, categoryIds }
          : draft;
    if (draftToSave !== draft) setDraft(draftToSave);
    const nextErrors = validateProcedure(draftToSave);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const missingFields = Object.keys(nextErrors).map((key) => {
        if (key === "title") return "procedure title";
        if (key === "category") return "category";
        if (key === "owner") return "owner";
        if (key.startsWith("heading:")) return "heading title";
        if (key.startsWith("section:")) return "section title";
        return "step properties";
      });
      const message = `Complete the required fields before saving: ${[
        ...new Set(missingFields),
      ].join(", ")}.`;
      setSaveError(message);
      toast.error(message, { icon: true });
      const firstErrorKey = Object.keys(nextErrors)[0];
      window.requestAnimationFrame(() => {
        const field = document.querySelector<HTMLElement>(
          `[data-procedure-error="${firstErrorKey}"]`,
        );
        field?.focus();
        field?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
      return;
    }
    if (!selectedCustomer || !selectedFacility) {
      const message = "Select a facility before saving this Procedure.";
      setSaveError(message);
      toast.error(message, { icon: true });
      return;
    }
    setSaving(true);
    setSaveError(null);
    void saveProcedureAuthoring(
      selectedCustomer.id,
      selectedFacility.id,
      savedProcedureId,
      draftToSave,
    )
      .then((savedProcedure) => {
        setSavedProcedureId(savedProcedure.id);
        setSavedSnapshot(JSON.stringify(draftToSave));
        setSavedNotice(true);
        if (aiDraftId) removeAiProcedureDraft(aiDraftId);
        if (aiDraftId && !afterSave && selectedCustomer && selectedFacility) {
          void navigate({
            replace: true,
            search: {
              ...getWorkScopeSearch(selectedCustomer, selectedFacility),
              procedureId: savedProcedure.id,
              aiDraftId: undefined,
            },
            to: "/procedures/authoring",
          });
        }
        toast.add("Procedure saved.", { icon: true });
        if (afterSave) afterSave();
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error
            ? error.message
            : "The procedure could not be saved.";
        setSaveError(message);
        toast.error(message, { icon: true });
      })
      .finally(() => setSaving(false));
  }

  const categoryOptions = categoryQuery.data ?? [];
  const categoryError = categoryQuery.error
    ? categoryQuery.error instanceof Error
      ? categoryQuery.error.message
      : "Unable to load categories."
    : null;
  const loading = Boolean(procedureId) && manualProcedureQuery.isPending;
  const loadError = manualProcedureQuery.error
    ? manualProcedureQuery.error instanceof Error
      ? manualProcedureQuery.error.message
      : "Unable to load this Procedure."
    : null;

  if (loading)
    return (
      <AppShellContent>
        <Stack gap={12}>
          <Skeleton height={24} width={180} />
          <Skeleton height={48} width="33%" />
          <Skeleton height={24} width="58%" />
          <Skeleton height={320} width="100%" />
        </Stack>
      </AppShellContent>
    );
  if (loadError)
    return (
      <AppShellContent>
        <Tile>
          <Stack gap={12}>
            <Title as="h1" size="lg">
              Unable to load Procedure
            </Title>
            <Text tone="subtle">{loadError}</Text>
            <Button
              onClick={() => backToLibrary(true)}
              size="sm"
              variant="outline"
            >
              Back to library
            </Button>
          </Stack>
        </Tile>
      </AppShellContent>
    );
  return (
    <AppShellContent>
      <Stack gap={16}>
        {aiDraftId || retainedSourceName ? (
          <Stack gap={8}>
            {aiDraftId ? (
              <Alert
                tone="informative"
                title="AI-assisted draft"
                subtext="Review and save this draft before attaching it to a Work Order."
              />
            ) : null}
            {retainedSourceName ? (
              <Alert
                tone="informative"
                title="Source material retained"
                subtext={`${retainedSourceName} was saved to Documents.`}
              />
            ) : null}
          </Stack>
        ) : null}
        <div className="self-start">
          <Button onClick={() => backToLibrary()} size="sm" variant="ghost">
            ← Back to library
          </Button>
        </div>
        <div className="grid gap-(--spacing-16) lg:grid-cols-[minmax(0,1fr)_340px]">
          <Stack gap={8}>
            <ProcedureAuthoringHeader
              categories={draft.categories}
              categoryOptions={categoryOptions}
              categoryError={categoryError ?? undefined}
              description={draft.description}
              editable={mode !== "preview"}
              errors={errors}
              onCategoriesChange={(next) =>
                patch({
                  categories: next,
                  category: next[0] ?? "",
                  categoryIds: next
                    .map(
                      (name) =>
                        categoryOptions.find((option) => option.name === name)
                          ?.id,
                    )
                    .filter((id): id is number => id !== undefined),
                })
              }
              onDescriptionChange={(description) => patch({ description })}
              onTitleChange={(title) => patch({ title })}
              title={draft.title}
            />
          </Stack>
          <Group align="start" gap={8} justify="end" wrap="wrap">
            <SegmentedFilter
              aria-label="Procedure editor mode"
              items={[
                { value: "edit", label: "Edit" },
                { value: "preview", label: "Preview" },
              ]}
              onValueChange={(value) => setMode(value as "edit" | "preview")}
              value={mode}
            />
            {mode !== "preview" ? (
              <Button
                disabled={(!isDirty && !isNewAiDraft) || saving}
                icon="check-mark"
                loading={saving}
                onClick={() => void save()}
                size="sm"
              >
                Save procedure
              </Button>
            ) : null}
            {savedNotice ? <Text tone="positive">Saved</Text> : null}
          </Group>
        </div>
        {saveError ? (
          <Alert
            tone="negative"
            title="Could not save procedure"
            subtext={saveError}
          />
        ) : null}
        {mode === "preview" ? (
          <ProcedurePreview draft={draft} />
        ) : (
          <ProcedureBuilderContent
            addChoice={addChoice}
            addHeading={addHeading}
            addItemAfter={addItemAfter}
            addNewItem={addNewItem}
            addSectionToGroup={addSectionToGroup}
            collapsedGroups={collapsedGroups}
            draft={draft}
            duplicateGroup={duplicateGroup}
            duplicateItemAndFocus={duplicateItemAndFocus}
            duplicateSection={duplicateSection}
            errors={errors}
            handleItemDragOver={handleItemDragOver}
            handleItemDrop={handleItemDrop}
            nextId={nextId}
            removeItem={removeItem}
            removeChoice={removeChoice}
            removeGroup={removeGroup}
            removeSection={removeSection}
            selectedItem={selectedItem}
            selectedItemId={selectedItemId}
            selectedStructure={selectedStructure}
            setCollapsedGroups={setCollapsedGroups}
            setDraft={setDraft}
            setDraggingItem={setDraggingItem}
            setSelectedItemId={setSelectedItemId}
            setSelectedStructure={setSelectedStructure}
          />
        )}
        <ProcedureDeleteDialog
          onDelete={() => {
            if (!pendingDelete) return;
            const request = pendingDelete;
            setDraft((current) =>
              request.kind === "group"
                ? {
                    ...current,
                    groups: current.groups.filter(
                      (group) => group.id !== request.id,
                    ),
                  }
                : {
                    ...current,
                    groups: request.groupId
                      ? current.groups.map((group) =>
                          group.id === request.groupId
                            ? {
                                ...group,
                                sections: group.sections.filter(
                                  (section) => section.id !== request.id,
                                ),
                              }
                            : group,
                        )
                      : current.groups,
                    looseSections: request.groupId
                      ? current.looseSections
                      : current.looseSections.filter(
                          (section) => section.id !== request.id,
                        ),
                  },
            );
            setPendingDelete(null);
          }}
          onKeepContents={() => {
            if (!pendingDelete) return;
            const request = pendingDelete;
            setDraft((current) =>
              request.kind === "group"
                ? deleteGroupKeepingContents(current, request.id)
                : deleteSectionKeepingContents(
                    current,
                    request.groupId,
                    request.id,
                  ),
            );
            setPendingDelete(null);
          }}
          onOpenChange={(open) => {
            if (!open) setPendingDelete(null);
          }}
          request={pendingDelete}
        />
        <ProcedureLeaveDialog
          onDiscard={() => {
            setLeavePrompt(false);
            const resolve = pendingNavigationResolve.current;
            pendingNavigationResolve.current = null;
            if (resolve) resolve(false);
            else {
              skipNextNavigation.current = true;
              backToLibrary(true);
            }
          }}
          onOpenChange={(open) => {
            setLeavePrompt(open);
            if (!open) {
              const resolve = pendingNavigationResolve.current;
              pendingNavigationResolve.current = null;
              if (resolve) resolve(true);
            }
          }}
          onSave={() => {
            const resolve = pendingNavigationResolve.current;
            if (resolve) {
              save(() => {
                pendingNavigationResolve.current = null;
                setLeavePrompt(false);
                resolve(false);
              });
            } else {
              setLeavePrompt(false);
              save(() => backToLibrary(true));
            }
          }}
          open={leavePrompt}
        />
      </Stack>
    </AppShellContent>
  );
}
